if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopNavigationQuery = window.matchMedia(
          '(min-width: 990px) and (hover: hover) and (pointer: fine)'
        );

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;

            button.addEventListener(
              'click',
              this.setActiveMedia.bind(
                this,
                mediaToSwitch.dataset.target,
                false
              )
            );
          });

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        this.handleDesktopArrowClick =
          this.onDesktopArrowClick.bind(this);
        this.handleDesktopKeydown =
          this.onDesktopKeydown.bind(this);
        this.handleDesktopModeChange =
          this.syncDesktopNavigation.bind(this);

        // Capture desktop arrow clicks before the original slider handles them.
        this.elements.thumbnails.addEventListener(
          'click',
          this.handleDesktopArrowClick,
          true
        );

        this.addEventListener(
          'keydown',
          this.handleDesktopKeydown
        );

        this.desktopArrowObserver = new MutationObserver(() => {
          this.enableDesktopArrows();
        });
      }

      connectedCallback() {
        if (!this.desktopArrowObserver) return;

        this.desktopNavigationQuery.addEventListener(
          'change',
          this.handleDesktopModeChange
        );

        this.syncDesktopNavigation();
      }

      disconnectedCallback() {
        if (!this.desktopArrowObserver) return;

        this.desktopArrowObserver.disconnect();

        this.desktopNavigationQuery.removeEventListener(
          'change',
          this.handleDesktopModeChange
        );
      }

      desktopNavigationEnabled() {
        return (
          this.desktopNavigationQuery.matches &&
          (this.dataset.desktopLayout || '').includes('thumbnail')
        );
      }

      getNavigableThumbnails() {
        if (!this.elements.thumbnails) return [];

        return Array.from(
          this.elements.thumbnails.querySelectorAll('[data-target]')
        ).filter((thumbnail) => {
          const button = thumbnail.querySelector('button');

          return (
            button &&
            !button.disabled &&
            thumbnail.getClientRects().length > 0 &&
            this.elements.viewer.querySelector(
              `[data-media-id="${thumbnail.dataset.target}"]`
            )
          );
        });
      }

      syncDesktopNavigation() {
        this.desktopArrowObserver.disconnect();

        if (this.desktopNavigationEnabled()) {
          this.desktopArrowObserver.observe(
            this.elements.thumbnails,
            {
              attributes: true,
              attributeFilter: ['disabled', 'aria-disabled'],
              subtree: true,
            }
          );

          this.enableDesktopArrows();
        } else {
          // Let the original slider restore its normal mobile arrow state.
          if (
            typeof this.elements.thumbnails.update === 'function'
          ) {
            this.elements.thumbnails.update();
          }
        }
      }

      enableDesktopArrows() {
        if (!this.desktopNavigationEnabled()) return;
        if (this.getNavigableThumbnails().length < 2) return;

        this.elements.thumbnails
          .querySelectorAll(
            'button[name="previous"], button[name="next"]'
          )
          .forEach((button) => {
            if (button.disabled) {
              button.disabled = false;
            }

            if (button.getAttribute('aria-disabled') === 'true') {
              button.removeAttribute('aria-disabled');
            }
          });
      }

      onDesktopArrowClick(event) {
        if (!this.desktopNavigationEnabled()) return;
        if (!(event.target instanceof Element)) return;

        const button = event.target.closest(
          'button[name="previous"], button[name="next"]'
        );

        if (
          !button ||
          !this.elements.thumbnails.contains(button) ||
          this.getNavigableThumbnails().length < 2
        ) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        this.moveDesktopMedia(
          button.name === 'next' ? 1 : -1
        );
      }

      onDesktopKeydown(event) {
        if (!this.desktopNavigationEnabled()) return;

        if (
          event.defaultPrevented ||
          event.altKey ||
          event.ctrlKey ||
          event.metaKey ||
          event.shiftKey ||
          !['ArrowLeft', 'ArrowRight'].includes(event.key)
        ) {
          return;
        }

        const target = event.target;
        if (!(target instanceof Element)) return;

        if (
          target.closest(
            'input, textarea, select, [contenteditable], ' +
            'video, audio, iframe, model-viewer, deferred-media'
          )
        ) {
          return;
        }

        if (this.getNavigableThumbnails().length < 2) return;

        event.preventDefault();
        event.stopPropagation();

        this.moveDesktopMedia(
          event.key === 'ArrowRight' ? 1 : -1
        );
      }

      moveDesktopMedia(direction) {
        const thumbnails = this.getNavigableThumbnails();
        if (thumbnails.length < 2) return;

        let currentIndex = thumbnails.findIndex((thumbnail) =>
          thumbnail.querySelector('button[aria-current="true"]')
        );

        if (currentIndex < 0) {
          const activeMedia = this.elements.viewer.querySelector(
            '[data-media-id].is-active'
          );

          currentIndex = thumbnails.findIndex(
            (thumbnail) =>
              thumbnail.dataset.target ===
              activeMedia?.dataset.mediaId
          );
        }

        if (currentIndex < 0) currentIndex = 0;

        const nextIndex =
          (currentIndex + direction + thumbnails.length) %
          thumbnails.length;

        this.setActiveMedia(
          thumbnails[nextIndex].dataset.target,
          false
        );
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement.firstChild !==
                activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          // Don't scroll if the image is already in view.
          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
          });

        thumbnail
          .querySelector('button')
          .setAttribute('aria-current', true);

        if (
          this.elements.thumbnails.isSlideVisible(thumbnail, 10)
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  );
}
